import { useAppAlert } from '@/app/AppAlertProvider'
import EligibilityConformation from '@/app/pages/family_application_renewal/_components/EligibilityConformation.tsx'
import { ChildrenProp } from '@/common/types_and_interfaces.ts'
import { cn } from '@/common/utilities.ts'
import {
  selectFamilyApplication,
  SubmitFamilyApplication,
} from '@/storage/applications/familyApplicationSlice'
import { selectProgramSettings } from '@/storage/program_settings/programSettingsSlice'
import { AppDispatch } from '@/storage/store'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import ApplicationInformation from './_components/ApplicationInformation'
import ApplicationSubmitted from './_components/ApplicationSubmitted'
import { AttestionsPage } from './_components/Attestations'
import { StudentList } from './_components/StudentList'
import { SetApplicationFlag, Student } from '@/apis/family_application/requests'
import { static_errors } from '@/app/common/constants'
import { MutationStatus } from '@tanstack/react-query'

type FormSteps =
  | 'application-information'
  | 'student-list'
  | 'student-confirm-eligibility'
  | 'student-attestations'
  | 'application-review'
  | 'application-submitted'

function FamilyApplicationRenewalPage() {
  const application = useSelector(selectFamilyApplication)
  const [formStep, setFormStep] = useState<FormSteps>('application-information')
  const [selectedStudentId, setSelectedStudentId] = useState<number>(0)
  const [studentMeetsAllCriterias, setStudentMeetsAllCriterias] = useState(true)
  const [studentFlagStatus, setStudentFlagStatus] =
    useState<MutationStatus>('idle')

  const programSettings = useSelector(selectProgramSettings)
  const { t: translate } = useTranslation()
  const dispatch = useDispatch<AppDispatch>()
  const { fireAlert } = useAppAlert()

  async function handleApplicationSubmit(selectedStudent: Student) {
    if (!studentMeetsAllCriterias) {
      setStudentFlagStatus('pending')
      const response = await SetApplicationFlag({
        application_id: application.application_id,
        global_application_step_code: 'student_self_eligibility_declaration',
        flag: 'student_external_renewal_ineligible_declaration',
        student_id: selectedStudent.id,
      })

      if (response.error) {
        fireAlert({
          variant: 'error',
          message: static_errors.unexpected_contact_support,
          confirmBtn: true,
          confirmBtnText: 'Ok',
        })
        setStudentFlagStatus('idle')
        return
      }
    }
    setStudentFlagStatus('idle')

    const response = await dispatch(
      SubmitFamilyApplication({
        application_id: application.application_id,
        selected_student_ids: [selectedStudentId],
      })
    )

    if (response.meta.requestStatus === 'fulfilled') {
      setFormStep('application-submitted')
    } else {
      await fireAlert({
        variant: 'error',
        message: translate('error_submitting_application'),
      })
    }
  }

  const selectedStudent = application.students.find(
    (student) => student.id === selectedStudentId
  )

  if (formStep === 'application-information') {
    return (
      <Container>
        <ApplicationInformation
          programSettings={programSettings}
          onContinueClick={() => {
            setFormStep('student-list')
          }}
        />
      </Container>
    )
  }

  if (formStep === 'student-list') {
    return (
      <Container className="max-w-2xl h-full mx-auto w-full items-center justify-center">
        <StudentList
          programSettings={programSettings}
          onRenewStudentClick={(studentId) => {
            setSelectedStudentId(studentId)
            setFormStep('student-confirm-eligibility')
          }}
          application={application}
        />
      </Container>
    )
  }

  if (formStep === 'student-confirm-eligibility') {
    if (selectedStudent == undefined) {
      setFormStep('student-list')
      return null
    }
    return (
      <Container>
        <EligibilityConformation
          goToNextStep={(meetsAllCriterias: boolean) => {
            setStudentMeetsAllCriterias(meetsAllCriterias)
            setFormStep('student-attestations')
          }}
        />
      </Container>
    )
  }

  if (formStep === 'student-attestations') {
    if (!selectedStudent) {
      throw new Error('No student selected')
    }

    return (
      <Container className="max-w-2xl">
        <AttestionsPage
          studentFlagRequestStatus={studentFlagStatus}
          onAttestationsFinished={() => {
            handleApplicationSubmit(selectedStudent)
          }}
          selectedStudent={selectedStudent}
          application={application}
        />
      </Container>
    )
  }

  if (formStep === 'application-submitted') {
    if (!selectedStudent) {
      throw new Error('No student selected')
    }
    return (
      <Container>
        <ApplicationSubmitted
          selectedStudent={selectedStudent}
          onContinueClick={() => setFormStep('student-list')}
        />
      </Container>
    )
  }
}

export default FamilyApplicationRenewalPage

const Container = ({
  children,
  className,
}: ChildrenProp & { className?: string }) => {
  return (
    <div className={cn('flex grow max-w-4xl mx-auto', className)}>
      {children}
    </div>
  )
}
